import { useState, useEffect, useCallback, useRef } from 'react';
import {
    ReferenceArrayInput,
    AutocompleteArrayInput,
    BooleanInput,
    SelectInput,
    useListContext,
    useGetList,
    useTranslate,
    useResourceContext,
} from 'react-admin';
import { Chip, Button, Box, FormControlLabel, Popper, Checkbox, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowUpCollapseIcon from '@/assets/icons/arrow-up-collapse-icon.svg?react';
import { DateRangeInput } from '@/components/global/inputs/DateRangeInput.jsx';
import { ReferenceArrayFilterSpinner } from '@/components/global/filters/ReferenceArrayFilterSpinner.jsx';
import { ReferenceArrayFilterChips } from '@/components/global/filters/ReferenceArrayFilterChips.jsx';
import { useSyncFilterFormValue } from '@/composables/useSyncFilterFormValue.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { globalStyles } from '@/themes/commonStyles/global.js';
import isEqual from 'lodash/isEqual';
import { getPeriodMaxDate } from '@/components/analytics/PeriodFilters.jsx';

const CheckboxesFilterPopper = props => <Popper {...props} className='popper--checkboxes-filter' />;

const StyledCheckboxesFilter = styled('div')(({ theme, displayDeleteIcon }) => ({
    '& .checkboxes-filter': {
        minWidth: '100px',
        position: 'relative',
        '&.open': {
            '& .MuiAutocomplete-root > .MuiFormControl-root.MuiFormControl-marginDense': {
                borderColor: Palette.black,
                boxShadow: `0 0 0 2px ${Palette.yellow.yellow200}`,
                '& .MuiFormLabel-root.Mui-focused': {
                    '& + .MuiInputBase-root,MuiOutlinedInput-root': {
                        outline: 'none !important',
                    },
                },
            },
            '& .has-external-chips': {
                display: 'flex',
                outline: '2px solid #FFDF7B',
                borderColor: '#000000',
            },
        },

        '& .has-external-chips': {
            borderRadius: '12px',
            border: '2px solid #E8E8E8',
            display: 'flex',
            alignItems: 'center',
            minWidth: '240px',
            margin: '4px 0px',
            '& + .checkboxes-filter__popup': {
                top: 'calc(100% + 2px)',
            },
            '& .reference-array-filter__chips': {
                margin: '0px',
                paddingRight: '16px !important',
                paddingLeft: '12px !important',
                top: '0px',
                gap: '0px',
                '&::after': {
                    content: '""',
                    width: '20px',
                    height: '28px',
                    position: 'absolute',
                    right: '0px',
                    top: '0px',
                    background: `linear-gradient(90deg, rgba(247, 247, 247, 0.00) 0%, ${Palette.gray.gray75} 100%)`,
                    zIndex: '2',
                    pointerEvents: 'none',
                },
            },
            '& .MuiAutocomplete-root': {
                minWidth: 'max-content',
                maxWidth: 'max-content',
                '& .MuiFormControl-root': {
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none !important',
                    position: 'unset',
                    margin: '0px',
                    minWidth: '40px',
                    '&::before, &::after': {
                        display: 'none',
                    },
                    '&:hover': {
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none !important',
                    },
                    '& .MuiFormLabel-root': {
                        top: '-12px',
                    },
                    '& .MuiInputBase-root': {
                        minWidth: 'max-content',
                        maxWidth: 'max-content',
                        outline: 'none !important',
                        paddingLeft: '0px !important',
                        paddingRight: '40px !important',
                        '& .MuiInputBase-input.MuiAutocomplete-input': {
                            minWidth: '2px !important',
                            margin: '0px !important',
                            '&:focus': {
                                minWidth: '100px !important',
                                maxWidth: '100px !important',
                            },
                        },
                    },
                },
            },
        },

        '& .checkboxes-filter__input.has-external-chips .MuiAutocomplete-tag': {
            display: 'none',
        },
        '& .reference-array-filter__chips': {
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '4px',
            margin: '10px 26px -34px 14px',
            minHeight: '24px',
            overflow: 'hidden',
            pointerEvents: 'none',
            position: 'relative',
            zIndex: 2,
            maxWidth: '530px',
            top: '6px',
            '& .MuiChip-root': {
                pointerEvents: 'auto',
                padding: '4px 5px 4px 8px',
                '& .MuiChip-label': {
                    padding: '0 2px 0 0',
                },
            },
        },
        '& .MuiAutocomplete-root': {
            ...globalStyles.arrayFilterAutocompleteInput,
            '& .MuiChip-root': {
                zIndex: '2',
                cursor: 'auto',
                padding: '4px 5px 4px 8px',
                '& .MuiChip-label': {
                    padding: '0 2px 0 0',
                },
                '& .MuiChip-deleteIcon': {
                    display: displayDeleteIcon ? 'block' : 'none',
                },
            },
        },
        '& .checkboxes-filter__popup': {
            zIndex: 10,
            position: 'absolute',
            top: '100%',
            background: Palette.white,
            width: '100%',
            boxShadow: Palette.boxShadow.elevation2,
            borderRadius: '12px',
            display: 'none',
            '&__checkboxes': {
                maxHeight: '228px',
                overflowX: 'auto',
                '& .MuiFormControlLabel-root': {
                    display: 'flex',
                    margin: '0 12px',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    [theme.breakpoints.down('md')]: {
                        margin: '0',
                    },
                    '&:hover': {
                        background: Palette.yellow.yellow50,
                        [theme.breakpoints.down('md')]: {
                            background: 'transparent',
                        },
                    },
                    '& .MuiCheckbox-root': {
                        padding: '0',
                        margin: '0 8px 0 0',
                        height: 'auto',
                    },
                    '& .MuiTypography-root': {
                        fontSize: '14px',
                    },
                },
            },
            '&::-webkit-scrollbar-track': {
                background: 'transparent',
            },
            '& .loading': {
                ...globalStyles.filterLoading,
            },
            '&.open': {
                display: 'block',
            },
            '& .MuiFormControl-root': {
                margin: 0,
                width: '100%',
                padding: '2px 12px 12px 12px',
                border: 'none',
                [theme.breakpoints.down('md')]: {
                    padding: '0 12px 12px 12px',
                },
                '& .MuiFormLabel-root': {
                    display: 'none',
                },
                '& .MuiFormGroup-root': {
                    '& .MuiFormControlLabel-root': {
                        margin: 0,
                        padding: '10px 12px',
                        marginBottom: '0px',
                        borderRadius: '8px',
                        '& .MuiCheckbox-root': {
                            padding: '0',
                            margin: '0 8px 0 0',
                            height: 'auto',
                        },
                        '& .MuiTypography-root': {
                            padding: '0px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        },
                        '&:hover': {
                            background: Palette.yellow.yellow200,
                            [theme.breakpoints.down('md')]: {
                                background: 'transparent',
                            },
                        },
                    },
                },
                '& .MuiTypography-root': {
                    fontSize: '14px',
                },
            },
            '& .actions': {
                padding: '20px 24px 24px 24px',
                position: 'sticky',
                bottom: '-1px',
                background: Palette.white,
                borderRadius: '0 0 12px 12px',
                [theme.breakpoints.down('md')]: {
                    position: 'relative',
                    padding: '20px 14px 24px 14px',
                },
            },
            '& .select-all': {
                padding: '12px 20px 8px 20px',
                borderBottom: `2px solid ${Palette.gray.gray75}`,
                margin: 0,
                width: '100%',
                marginBottom: '12px',
                [theme.breakpoints.down('md')]: {
                    borderColor: 'transparent',
                    maxWidth: 'calc(100% - 36px)',
                    padding: '10px 12px',
                    margin: '0 10px',
                    '&::before': {
                        content: '""',
                        width: '100%',
                        height: '2px',
                        left: 0,
                        background: Palette.gray.gray75,
                        position: 'absolute',
                        top: '47px',
                        zIndex: 1,
                    },
                },
                '& .MuiTypography-root': {
                    fontSize: '14px',
                    paddingLeft: '4px',
                },
            },
            '& .MuiCheckbox-root': {
                padding: '4px',
            },
        },
        '& .arrow-up-collapse-icon': {
            position: 'absolute',
            right: '19px',
            top: '15px',
            zIndex: '2',
            height: '25px',
            borderRadius: '0',
            boxShadow: 'none',
            '& svg': {
                marginTop: '3px',
            },
        },
    },
}));

export const QuickFilter = ({ source, label }) => <Chip sx={{ marginBottom: 1 }} label={label} source={source} />;

QuickFilter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};

export const ReferenceArrayFilter = ({
    source,
    label,
    reference,
    optionText,
    inputText,
    optionValue,
    searchFields,
    getSearchFilter,
    filter = {},
    displayDeleteIcon = true,
    forceDesktopOnMobile = false,
    perPage,
}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const translate = useTranslate();
    const { setFilters, filterValues, displayedFilters } = useListContext();
    useSyncFilterFormValue(source);
    const [searchText, setSearchText] = useState('');
    const buildSearchFilter = (text, fields) => {
        if (!text) {
            return {};
        }
        if (getSearchFilter) {
            return getSearchFilter(text);
        }
        return {
            search: {
                value: text,
                fields: Object.fromEntries(fields.map(field => [field, true])),
            },
        };
    };
    const searchFilter = buildSearchFilter(searchText, searchFields || []);

    const combinedFilter = {
        ...filter,
        ...searchFilter,
    };
    const { data, isLoading } = useGetList(reference, {
        filter: combinedFilter,
        ...(perPage ? { pagination: { page: 1, perPage } } : {}),
    });
    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(filterValues[source] || []);
    const [pendingSelectedIds, setPendingSelectedIds] = useState(filterValues[source] || []);
    const [inputFocused, setInputFocused] = useState(false);
    const [selectedItemsData, setSelectedItemsData] = useState([]);
    const containerRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const mobileMode = isMobile && !forceDesktopOnMobile;

    const renderCheckbox = (choice, keyPrefix = '') => {
        const choiceId = optionValue ? choice[optionValue] : choice.id;
        const choiceText = typeof optionText === 'function' ? optionText(choice) : choice[optionText || 'name'];

        return (
            <FormControlLabel
                key={`${keyPrefix}${choiceId}`}
                control={
                    <Checkbox
                        checked={pendingSelectedIds.includes(choiceId)}
                        onChange={() => {
                            const newValue = pendingSelectedIds.includes(choiceId)
                                ? pendingSelectedIds.filter(id => id !== choiceId)
                                : [...pendingSelectedIds, choiceId];

                            const choiceAlreadySelected = selectedItemsData.some(item => (optionValue ? item[optionValue] : item.id) === choiceId);
                            const newSelectedData =
                                pendingSelectedIds.includes(choiceId) || choiceAlreadySelected ? selectedItemsData : [...selectedItemsData, choice];

                            setPendingSelectedIds(newValue);
                            setSelectedItemsData(newSelectedData);
                        }}
                    />
                }
                label={choiceText}
            />
        );
    };

    const toggleSelectAll = () => {
        const currentDataIds = data?.map(item => (optionValue ? item[optionValue] : item.id)) || [];
        const selectedDataIds = selectedItemsData?.map(item => (optionValue ? item[optionValue] : item.id)) || [];
        const allIds = [...new Set([...currentDataIds, ...selectedDataIds])];

        const allSelected = allIds.every(id => pendingSelectedIds.includes(id));

        if (allSelected) {
            setPendingSelectedIds([]);
        } else {
            setPendingSelectedIds(allIds);
            const existingIds = new Set(selectedItemsData.map(item => (optionValue ? item[optionValue] : item.id)));
            const newItems = (data || []).filter(item => !existingIds.has(optionValue ? item[optionValue] : item.id));
            setSelectedItemsData([...selectedItemsData, ...newItems]);
        }
    };

    const handleApply = () => {
        setSelectedIds(pendingSelectedIds);
        setFilters({ ...filterValues, [source]: pendingSelectedIds }, displayedFilters, false);
        setOpen(false);
    };

    const handleCancel = useCallback(() => {
        setPendingSelectedIds(selectedIds);
        setOpen(false);
    }, [selectedIds]);

    const handleClickOutside = useCallback(
        event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                handleCancel();
            }
        },
        [handleCancel],
    );

    useEffect(() => {
        const currentValue = filterValues[source] || [];
        if (!isEqual(selectedIds, currentValue)) {
            setSelectedIds(currentValue);
            setPendingSelectedIds(currentValue);
        }
    }, [filterValues, source, selectedIds]);

    useEffect(() => {
        if (data) {
            setSelectedItemsData(prevSelectedData => {
                if (prevSelectedData.length === 0) return prevSelectedData;

                return prevSelectedData.map(selectedItem => {
                    const selectedId = optionValue ? selectedItem[optionValue] : selectedItem.id;
                    const currentItem = data.find(item => (optionValue ? item[optionValue] : item.id) === selectedId);
                    return currentItem || selectedItem;
                });
            });
        }
    }, [data, optionValue]);

    useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            setSearchText('');
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, handleClickOutside]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const id = requestAnimationFrame(() => {
            const input = containerRef.current?.querySelector('.checkboxes-filter__input .MuiAutocomplete-root input');
            input?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    const selectedFilterIds = filterValues[source] || [];
    const hasExternalChips = selectedFilterIds.length > 0;

    const handleRemoveChip = idToRemove => {
        const newIds = selectedFilterIds.filter(id => String(id) !== String(idToRemove));
        const updatedFilters = { ...filterValues };

        if (newIds.length === 0) {
            delete updatedFilters[source];
        } else {
            updatedFilters[source] = newIds;
        }

        setFilters(updatedFilters, displayedFilters, false);
    };

    if (isLoading && !inputFocused) {
        return <ReferenceArrayFilterSpinner />;
    }

    return (
        <StyledCheckboxesFilter ref={containerRef} displayDeleteIcon={displayDeleteIcon}>
            <ReferenceArrayInput
                key={`${source}:${selectedFilterIds.map(String).join(',')}`}
                source={source}
                label={label}
                reference={reference}
                perPage={25}
                filter={filter}
            >
                <Box className={`checkboxes-filter ${open ? 'open' : ''} ${mobileMode ? 'mobile' : ''}`}>
                    <Box
                        className={`checkboxes-filter__input ${hasExternalChips ? 'has-external-chips' : ''}`}
                        onClick={() => {
                            setPendingSelectedIds(selectedIds);
                            setOpen(true);
                            if (open) {
                                const inputElement = containerRef.current?.querySelector('input');
                                setOpen(false);
                                if (inputElement) {
                                    inputElement.blur();
                                }
                            }
                        }}
                    >
                        {hasExternalChips && (
                            <ReferenceArrayFilterChips
                                source={source}
                                reference={reference}
                                optionText={optionText}
                                optionValue={optionValue}
                                displayDeleteIcon={displayDeleteIcon}
                                onRemove={handleRemoveChip}
                            />
                        )}
                        <AutocompleteArrayInput
                            fullWidth
                            source={source}
                            label={label}
                            optionText={optionText}
                            renderTags={() => null}
                            TextFieldProps={{
                                InputLabelProps: {
                                    shrink: hasExternalChips || open || inputFocused,
                                },
                            }}
                            inputText={inputText}
                            optionValue={optionValue}
                            filterToQuery={text => {
                                setSearchText(text);
                                return buildSearchFilter(text, searchFields || ['name']);
                            }}
                            onFocus={() => setInputFocused(true)}
                            onBlur={() => setInputFocused(false)}
                            PopperComponent={CheckboxesFilterPopper}
                        />
                        {!displayDeleteIcon && (
                            <Box className='arrow-up-collapse-icon'>
                                <ArrowUpCollapseIcon />
                            </Box>
                        )}
                    </Box>
                    {open && (
                        <Box className='checkboxes-filter__popup open'>
                            {isLoading && (
                                <Box className='loading'>
                                    <CircularProgress /> {translate('common.loading')}
                                </Box>
                            )}
                            {!isLoading && data?.length === 0 && (
                                <NotificationBox variant='warning' style={{ margin: '20px' }}>
                                    {translate('common.noResults')}
                                </NotificationBox>
                            )}
                            {!isLoading && data?.length !== 0 && (
                                <>
                                    <FormControlLabel
                                        className='select-all'
                                        label={translate('common.filter.selectAll')}
                                        control={
                                            <Checkbox
                                                onChange={toggleSelectAll}
                                                checked={(() => {
                                                    const currentDataIds = data?.map(item => (optionValue ? item[optionValue] : item.id)) || [];
                                                    const selectedDataIds = selectedItemsData?.map(item => (optionValue ? item[optionValue] : item.id)) || [];
                                                    const allIds = [...new Set([...currentDataIds, ...selectedDataIds])];
                                                    return allIds.length > 0 && allIds.every(id => pendingSelectedIds.includes(id));
                                                })()}
                                            />
                                        }
                                    />
                                    <Box className='checkboxes-filter__popup__checkboxes'>
                                        {!searchText && selectedItemsData.map(choice => renderCheckbox(choice, 'selected-'))}

                                        {data
                                            ?.filter(choice => {
                                                const choiceId = optionValue ? choice[optionValue] : choice.id;
                                                const selectedIds = selectedItemsData.map(item => (optionValue ? item[optionValue] : item.id));
                                                return !selectedIds.includes(choiceId);
                                            })
                                            .map(choice => renderCheckbox(choice))}
                                    </Box>
                                    <Box className='actions'>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            fullWidth
                                            onClick={handleApply}
                                            disabled={isEqual(selectedIds, pendingSelectedIds)}
                                        >
                                            {translate('common.apply')}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            </ReferenceArrayInput>
        </StyledCheckboxesFilter>
    );
};

ReferenceArrayFilter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
    optionText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    optionValue: PropTypes.string,
    searchFields: PropTypes.arrayOf(PropTypes.string),
    getSearchFilter: PropTypes.func,
    filter: PropTypes.object,
    displayDeleteIcon: PropTypes.bool,
    forceDesktopOnMobile: PropTypes.bool,
    perPage: PropTypes.number,
};

export const BooleanFilter = ({ source, label }) => <BooleanInput source={source} label={label} sx={{ marginBottom: 1 }} />;

BooleanFilter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};

export const AutocompleteArrayFilter = ({ source, label, choices }) => (
    <AutocompleteArrayInput source={source} label={label} choices={choices} fullWidth={true} />
);

AutocompleteArrayFilter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    choices: PropTypes.array.isRequired,
};

export const SelectFilter = ({ source, label, choices }) => <SelectInput source={source} label={label} choices={choices} fullWidth={true} />;

SelectFilter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    choices: PropTypes.array.isRequired,
};

export const Select1or0Filter = ({ source, label, emptyText }) => {
    const translate = useTranslate();
    const defaultEmptyText = emptyText || translate('common.all');

    return (
        <SelectInput
            source={source}
            label={label}
            choices={[
                { id: 1, name: translate('common.boolean.yes') },
                { id: 0, name: translate('common.boolean.no') },
            ]}
            emptyText={defaultEmptyText}
        />
    );
};

Select1or0Filter.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    emptyText: PropTypes.string,
};

export const PeriodFilter = () => {
    const { filterValues, setFilters } = useListContext();
    const resource = useResourceContext();
    const maxDate = getPeriodMaxDate(resource);

    const handleDateRangeChange = useCallback(
        ({ start, end }) => {
            setFilters(
                {
                    ...filterValues,
                    period: 'custom',
                    periodStart_gte: start,
                    periodEnd_lte: end,
                },
                null,
                false,
            );
        },
        [filterValues, setFilters],
    );

    return (
        'custom' === filterValues.period && (
            <DateRangeInput onChange={handleDateRangeChange} start={filterValues.periodStart_gte} end={filterValues.periodEnd_lte} maxDate={maxDate} />
        )
    );
};

const servicedStatusIds = ['waitingForTriage', 'serviceInspection', 'new', 'failedContact', 'accepted', 'pending', 'inProgress', 'fixed', 'closed'];

export const mappedFilters = {
    serviceRequestType: {
        key: 'type_in',
        mapping: {
            all: null,
            softwareFailure: ['softwareFailure'],
            mechanicalFailure: ['mechanicalFailure', 'vandalismAct', 'productionDefect'],
        },
    },
    serviceStatus: {
        key: 'serviceStatus_in',
        mapping: {
            all: null,
            ok: ['ok'],
            serviced: servicedStatusIds,
        },
    },
    productWithTags: {
        key: 'tagsCount',
        mapping: {
            0: 0,
            1: { value: 1, operator: 'gte' },
        },
    },
};
